import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ShoppingCart, Loader2, Eye, RefreshCw, Truck, User, Clock, ChefHat, DollarSign, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const statusOptions = [
  { value: 'all', labelKey: 'common.all' },
  { value: 'pending', labelKey: 'orders.pending' },
  { value: 'confirmed', labelKey: 'orders.confirmed' },
  { value: 'preparing', labelKey: 'orders.preparing' },
  { value: 'ready', labelKey: 'orders.ready' },
  { value: 'out_for_delivery', labelKey: 'orders.outForDelivery' },
  { value: 'delivered', labelKey: 'orders.delivered' },
  { value: 'cancelled', labelKey: 'orders.cancelled' },
];

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  confirmed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  preparing: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  out_for_delivery: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  delivered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const statusLabels = {
  pending: 'orders.pending', confirmed: 'orders.confirmed', preparing: 'orders.preparing',
  ready: 'orders.ready', out_for_delivery: 'orders.outForDelivery',
  delivered: 'orders.delivered', cancelled: 'orders.cancelled',
};

export default function OrdersPage() {
  const { activeBranch, authHeaders, API } = useAuth();
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => { fetchOrders(); }, [activeBranch, filterStatus]);

  const fetchOrders = async () => {
    if (!activeBranch) { setLoading(false); return; }
    setLoading(true);
    try {
      let url = `${API}/orders?branch_id=${activeBranch.id}`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      const [ordersRes, driversRes] = await Promise.all([
        axios.get(url, authHeaders()),
        axios.get(`${API}/drivers?branch_id=${activeBranch.id}`, authHeaders()).catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data);
      setDrivers(Array.isArray(driversRes.data) ? driversRes.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, authHeaders());
      toast.success(t('orders.statusUpdated'));
      fetchOrders();
    } catch (err) { toast.error(t('orders.updateFailed')); }
  };

  const viewDetail = (order) => {
    setSelectedOrder(order);
    setDetailOpen(true);
  };

  const getDriverName = (driverId) => {
    if (!driverId) return null;
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || null;
  };

  const nextStatus = (current) => {
    const flow = { pending: 'confirmed', confirmed: 'preparing', preparing: 'ready' };
    return flow[current] || null;
  };

  return (
    <ModuleLayout moduleKey="operations">
      <div className="space-y-6" data-testid="orders-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl text-foreground" style={{ fontFamily: 'Tajawal, sans-serif' }}>{t('nav.orders')}</h1>
            <p className="text-sm text-muted-foreground">{t('orders.manageOrders')}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]" data-testid="orders-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchOrders} data-testid="orders-refresh-btn" className="h-9">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Cross-section: Order Stats Banner */}
        {!loading && orders.length > 0 && (() => {
          const pending = orders.filter(o => o.status === 'pending').length;
          const preparing = orders.filter(o => o.status === 'preparing').length;
          const todayOrders = orders.length;
          const todayRevenue = orders.reduce((s, o) => s + (o.total_price || 0), 0);
          return (
            <div data-testid="orders-stats-banner" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card">
                <div className="p-2 rounded-lg bg-blue-500/10"><ShoppingCart className="w-4 h-4 text-blue-500" /></div>
                <div><div className="text-lg font-bold">{todayOrders}</div><div className="text-[11px] text-muted-foreground">{t('crossSection.todayOrders')}</div></div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card">
                <div className="p-2 rounded-lg bg-emerald-500/10"><DollarSign className="w-4 h-4 text-emerald-500" /></div>
                <div><div className="text-lg font-bold">{todayRevenue.toLocaleString()}</div><div className="text-[11px] text-muted-foreground">{t('crossSection.todayRevenue')}</div></div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card">
                <div className="p-2 rounded-lg bg-yellow-500/10"><Clock className="w-4 h-4 text-yellow-500" /></div>
                <div><div className="text-lg font-bold">{pending}</div><div className="text-[11px] text-muted-foreground">{t('crossSection.pendingCount')}</div></div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card">
                <div className="p-2 rounded-lg bg-orange-500/10"><ChefHat className="w-4 h-4 text-orange-500" /></div>
                <div>
                  <div className="text-lg font-bold">{preparing}</div>
                  <div className="text-[11px] text-muted-foreground">{t('crossSection.preparingCount')}</div>
                </div>
              </div>
            </div>
          );
        })()}

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{t('orders.noOrders')}</p>
          </div>
        ) : (
          <Card className="bg-card border border-border/50 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('orders.customer')}</TableHead>
                  <TableHead>{t('orders.itemsCount')}</TableHead>
                  <TableHead>{t('orders.total')}</TableHead>
                  <TableHead>{t('orders.status')}</TableHead>
                  <TableHead>{t('orders.driver')}</TableHead>
                  <TableHead className="text-left">{t('orders.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => {
                  const driverName = getDriverName(order.driver_id);
                  return (
                    <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-foreground">{order.customer_name}</p>
                            {order.is_dine_in && (
                              <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-500/30 text-amber-600 dark:text-amber-400 bg-amber-500/5" data-testid={`dine-in-badge-${order.id}`}>
                                {t('orders.dineIn')} {order.table_number ? `#${order.table_number}` : ''}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground" dir="ltr">{order.customer_phone}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {order.items?.length || 0} {t('fleet.items')}
                      </TableCell>
                      <TableCell className="font-medium text-sm">{order.total_price} {t('common.sar')}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${statusColors[order.status] || ''}`}>
                          {t(statusLabels[order.status] || order.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {driverName ? (
                          <div className="flex items-center gap-1.5 text-sm">
                            <Truck className="w-3.5 h-3.5 text-purple-500" />
                            <span className="text-foreground">{driverName}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => viewDetail(order)} data-testid={`view-order-${order.id}`} className="h-7 w-7 p-0">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {nextStatus(order.status) && (
                            <Button size="sm" onClick={() => updateStatus(order.id, nextStatus(order.status))} data-testid={`advance-order-${order.id}`} className="h-7 text-xs px-2 rounded-full">
                              {t(statusLabels[nextStatus(order.status)])}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Order Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Tajawal, sans-serif' }}>{t('orders.orderDetails')}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">{t('orders.customer')}:</span><p className="font-medium">{selectedOrder.customer_name}</p></div>
                  <div><span className="text-muted-foreground">{t('orders.phone')}:</span><p className="font-medium" dir="ltr">{selectedOrder.customer_phone}</p></div>
                  <div><span className="text-muted-foreground">{t('orders.address')}:</span><p className="font-medium">{selectedOrder.customer_address || '-'}</p></div>
                  <div><span className="text-muted-foreground">{t('orders.status')}:</span><p><Badge className={`text-xs ${statusColors[selectedOrder.status] || ''}`}>{t(statusLabels[selectedOrder.status] || selectedOrder.status)}</Badge></p></div>
                </div>
                {/* Order type badge */}
                {selectedOrder.is_dine_in && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-center gap-2" data-testid="order-dine-in-info">
                    <span className="text-lg">🪑</span>
                    <div>
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">{t('orders.dineIn')} {selectedOrder.table_number ? `- ${t('orders.table')} #${selectedOrder.table_number}` : ''}</p>
                      <p className="text-xs text-muted-foreground">{t('orders.noParkingDelivery')}</p>
                    </div>
                  </div>
                )}
                {/* Driver info */}
                {selectedOrder.driver_id && (
                  <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-purple-500" />
                      <span className="font-medium">{t('orders.driver')}:</span>
                      <span>{getDriverName(selectedOrder.driver_id) || selectedOrder.driver_id.slice(0, 8)}</span>
                    </div>
                    {selectedOrder.delivery_status && (
                      <p className="text-xs text-muted-foreground mt-1">{t('orders.deliveryStatus')}: {t(statusLabels[selectedOrder.delivery_status] || selectedOrder.delivery_status)}</p>
                    )}
                  </div>
                )}
                <div className="border-t border-border pt-3">
                  <p className="text-sm font-medium mb-2">{t('orders.itemsList')}:</p>
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm py-1">
                      <span>{item.name_ar || item.name} x{item.quantity}</span>
                      <span className="text-muted-foreground">{item.price} {t('common.sar')}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border pt-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">{t('orders.subtotal')}:</span><span>{selectedOrder.subtotal} {t('common.sar')}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t('orders.delivery')}:</span><span>{selectedOrder.delivery_fee} {t('common.sar')}</span></div>
                  <div className="flex justify-between font-bold"><span>{t('orders.total')}:</span><span>{selectedOrder.total_price} {t('common.sar')}</span></div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ModuleLayout>
  );
}
